/**
 * bootStage.js
 *
 * Tracks where the SPA is in its startup/render lifecycle so error screens
 * and console logs can answer "where did it die?" without guessing.
 *
 * Works outside React (MSAL init in main.jsx) and inside via useBootStage().
 */

const MAX_HISTORY = 12;

/** @type {{ id: string, label: string, detail: string|null, at: number }[]} */
let _history = [];

/** @type {{ id: string, label: string, detail: string|null, at: number }} */
let _current = { id: 'boot', label: 'Starting', detail: null, at: Date.now() };

/** @type {Set<() => void>} */
const _listeners = new Set();

function notify() {
  for (const fn of _listeners) {
    try { fn(); } catch { /* listener must not break boot */ }
  }
}

/**
 * Record the current boot/render stage.
 * @param {string} id     stable machine id, e.g. 'auth.warming'
 * @param {string} label  human-readable headline
 * @param {string|null} [detail] optional sub-step, e.g. 'Loading cases…'
 */
export function setBootStage(id, label, detail = null) {
  _current = { id, label, detail, at: Date.now() };
  _history = [..._history, _current].slice(-MAX_HISTORY);
  notify();
}

/** @returns {{ id: string, label: string, detail: string|null, at: number }} */
export function getBootStage() {
  return _current;
}

/** @returns {typeof _history} */
export function getBootHistory() {
  return _history;
}

/** Subscribe to stage changes (for React useSyncExternalStore). */
export function subscribeBootStage(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

/** Snapshot getter for useSyncExternalStore — returns a version token. */
export function getBootStageVersion() {
  return _current.at;
}

/** Format a diagnostics block for error UI / console. */
export function formatBootDiagnostics({ error, componentStack, stageId, stageLabel } = {}) {
  const lines = [];
  const stage = getBootStage();

  lines.push(`Stage: ${stageId || stage.id} — ${stageLabel || stage.label}`);
  if (stage.detail) lines.push(`Detail: ${stage.detail}`);
  if (error?.message) lines.push(`Error: ${error.message}`);

  const trail = getBootHistory();
  if (trail.length > 1) {
    lines.push('');
    lines.push('Recent stages:');
    for (const s of trail.slice(-6)) {
      const ago = Math.max(0, Math.round((Date.now() - s.at) / 1000));
      const extra = s.detail ? ` (${s.detail})` : '';
      lines.push(`  • ${s.id} — ${s.label}${extra} [${ago}s ago]`);
    }
  }

  if (componentStack) {
    lines.push('');
    lines.push('Component stack:');
    lines.push(componentStack.trim());
  }

  return lines.join('\n');
}
